import type { InternalAxiosRequestConfig } from "axios";
import axios from "../axios";
import axiosWithoutInterceptor from "../axiosWithoutInterceptor";
import getConfig from "../config";
import isOneWebDomainRuntime from "./avaAppRuntime";

const TOKEN_PATH = "/api/token/exemptions";

let inFlightTokenPromise: Promise<string> | null = null;
let interceptorsRegistered = false;

const TOKEN_FETCH_TIMEOUT_MS = 5_000;
const MAX_TOKEN_FETCH_FAILURES = 3;
const TOKEN_CIRCUIT_COOLDOWN_MS = 30_000;

const tokenFetchCircuit = { failures: 0, openUntil: 0 };

/**
 * Returns true when the request targets the ECM API and should carry an
 * AvaApp-issued bearer token (OWD only).
 */
export const isAvaAppEcmApiRequest = (url: string | undefined): boolean => {
    if (!url) {
        return false;
    }

    const { ecmApiHost } = getConfig().hostnames;
    if (!ecmApiHost) {
        return false;
    }

    return url.includes(ecmApiHost) && url.includes("/api/");
};

/**
 * Fetches a fresh access token from the AvaApp shell. Concurrent callers share
 * a single in-flight request. AvaApp tokens have a short rolling lifetime, so
 * callers should fetch per protected ECM request rather than schedule refresh.
 */
export const fetchAvaAppAccessToken = async (): Promise<string> => {
    if (!isOneWebDomainRuntime()) {
        throw new Error("fetchAvaAppAccessToken called outside One Web Domain runtime");
    }

    if (
        tokenFetchCircuit.failures >= MAX_TOKEN_FETCH_FAILURES &&
        Date.now() < tokenFetchCircuit.openUntil
    ) {
        throw new Error("AvaApp token endpoint unavailable (circuit open)");
    }

    if (!inFlightTokenPromise) {
        inFlightTokenPromise = (async () => {
            const { avaApp } = getConfig().hostnames;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), TOKEN_FETCH_TIMEOUT_MS);

            try {
                const response = await axiosWithoutInterceptor.get<{ accessToken?: string }>(
                    `${avaApp}${TOKEN_PATH}`,
                    { withCredentials: true, signal: controller.signal }
                );

                const token = response?.data?.accessToken;
                if (!token) {
                    throw new Error("Failed to retrieve AvaApp access token");
                }

                tokenFetchCircuit.failures = 0;
                tokenFetchCircuit.openUntil = 0;
                return token;
            } catch (err) {
                tokenFetchCircuit.failures++;
                if (tokenFetchCircuit.failures >= MAX_TOKEN_FETCH_FAILURES) {
                    tokenFetchCircuit.openUntil = Date.now() + TOKEN_CIRCUIT_COOLDOWN_MS;
                }
                throw err;
            } finally {
                clearTimeout(timeoutId);
            }
        })().finally(() => {
            inFlightTokenPromise = null;
        });
    }

    return inFlightTokenPromise;
};

const attachAvaAppTokenToRequest = async (
    config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
    if (!isAvaAppEcmApiRequest(config.url)) {
        return config;
    }

    const token = await fetchAvaAppAccessToken();
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
    return config;
};

/**
 * Registers request interceptors on both axios instances so every ECM API call
 * in AvaApp uses a freshly issued shell token before the request is sent.
 */
export const setupAvaAppTokenInterceptors = (): void => {
    if (!isOneWebDomainRuntime() || interceptorsRegistered) {
        return;
    }

    axios.interceptors.request.use(attachAvaAppTokenToRequest);
    axiosWithoutInterceptor.interceptors.request.use(attachAvaAppTokenToRequest);
    interceptorsRegistered = true;
};
